import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  LineStyle as LcLineStyle,
  PriceScaleMode,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { CandlePoint, ChartHostApi, LineSpec, LineStyle, SetLinesOpts, VolumePoint } from '@sdk';
import { debugLog } from './debug';

const BG = '#07080b';
const TEXT = '#c5cdc8';
const GRID = '#161c22';
const CROSS = '#3ee58b';

function lineStyleOf(style?: LineStyle): LcLineStyle {
  if (style === 'dashed') return LcLineStyle.Dashed;
  if (style === 'dotted') return LcLineStyle.Dotted;
  return LcLineStyle.Solid;
}

function asDay(t: string): string | null {
  const d = String(t || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

function sanitizeLine(points: { time: string; value: number }[]) {
  const out: { time: string; value: number }[] = [];
  let last = '';
  for (const p of points) {
    const time = asDay(p.time);
    if (!time || time <= last) continue;
    if (!Number.isFinite(p.value)) continue;
    last = time;
    out.push({ time, value: p.value });
  }
  return out;
}

function sanitizeCandles(bars: CandlePoint[]) {
  const out: CandlePoint[] = [];
  let last = '';
  for (const b of bars) {
    const time = asDay(b.time);
    if (!time || time <= last) continue;
    if (![b.open, b.high, b.low, b.close].every((n) => Number.isFinite(n))) continue;
    last = time;
    out.push({ ...b, time });
  }
  return out;
}

function baseOptions() {
  return {
    autoSize: true as const,
    layout: {
      background: { type: ColorType.Solid, color: BG },
      textColor: TEXT,
      fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
      fontSize: 11,
    },
    grid: {
      vertLines: { color: GRID },
      horzLines: { color: GRID },
    },
    crosshair: {
      mode: CrosshairMode.Normal,
      vertLine: { color: CROSS, width: 1 as const, labelBackgroundColor: '#145c3a' },
      horzLine: { color: CROSS, width: 1 as const, labelBackgroundColor: '#145c3a' },
    },
    rightPriceScale: { borderColor: GRID },
    timeScale: { borderColor: GRID, timeVisible: false },
  };
}

function paintLegend(el: HTMLElement, items: { title: string; color: string }[]) {
  el.replaceChildren();
  for (const item of items) {
    const row = document.createElement('span');
    row.className = 'legend-item';
    row.innerHTML = `<i style="background:${item.color}"></i>${item.title}`;
    el.append(row);
  }
}

export function createChartHost(container: HTMLElement): ChartHostApi {
  container.classList.add('chart-host');
  const canvas = document.createElement('div');
  canvas.className = 'chart-canvas';
  const legend = document.createElement('div');
  legend.className = 'chart-legend';
  container.replaceChildren(canvas, legend);

  let chart: IChartApi | null = null;

  function resetChart(): IChartApi {
    if (chart) {
      chart.remove();
      chart = null;
    }
    canvas.replaceChildren();
    chart = createChart(canvas, baseOptions());
    return chart;
  }

  const api: ChartHostApi = {
    clear() {
      if (chart) {
        chart.remove();
        chart = null;
      }
      canvas.replaceChildren();
      legend.replaceChildren();
      debugLog('info', 'chart', 'cleared');
    },

    setCandles(bars: CandlePoint[], volume?: VolumePoint[]) {
      const clean = sanitizeCandles(bars);
      debugLog('info', 'chart', 'setCandles', { in: bars.length, kept: clean.length, sample: clean[0] });
      const c = resetChart();
      paintLegend(legend, [{ title: 'price', color: '#3ee58b' }]);
      const candles = c.addSeries(CandlestickSeries, {
        upColor: '#3ee58b',
        downColor: '#ff5d73',
        borderVisible: false,
        wickUpColor: '#3ee58b',
        wickDownColor: '#ff5d73',
      });
      candles.setData(clean.map((b) => ({ ...b, time: b.time as UTCTimestamp | string })));
      if (volume && volume.length) {
        const vol = c.addSeries(HistogramSeries, {
          priceFormat: { type: 'volume' },
          priceScaleId: 'volume',
        });
        c.priceScale('volume').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
        vol.setData(
          volume
            .map((v) => ({ time: asDay(v.time), value: v.value, color: v.color }))
            .filter((v) => v.time) as { time: string; value: number; color: string }[],
        );
      }
      c.timeScale().fitContent();
    },

    setLines(lines: LineSpec[], opts?: SetLinesOpts) {
      const prepared = lines.map((line) => ({
        ...line,
        data: sanitizeLine(line.data),
      }));
      debugLog('info', 'chart', 'setLines', {
        logScale: Boolean(opts?.logScale),
        series: prepared.map((l) => ({
          id: l.id,
          title: l.title,
          pane: l.pane ?? 0,
          n: l.data.length,
          first: l.data[0],
          last: l.data[l.data.length - 1],
        })),
      });
      const usable = prepared.filter((l) => l.data.length >= 2);
      if (!usable.length) {
        debugLog('error', 'chart', 'setLines: no usable series after sanitize');
        throw new Error('No plottable series (dates/values were invalid). Open debug and copy the report.');
      }

      const c = resetChart();
      paintLegend(
        legend,
        usable.filter((l) => (l.pane ?? 0) === 0).map((l) => ({ title: l.title, color: l.color })),
      );

      let paneOk = true;
      if (usable.some((l) => (l.pane ?? 0) === 1)) {
        try {
          const pane = c.addPane();
          pane.setStretchFactor(0.4);
        } catch (err) {
          paneOk = false;
          debugLog('warn', 'chart', 'addPane failed; drawing drawdown on pane 0', {
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }

      for (const line of usable) {
        const pane = paneOk ? (line.pane ?? 0) : 0;
        const s: ISeriesApi<'Line'> = c.addSeries(
          LineSeries,
          {
            color: line.color,
            lineWidth: (line.lineWidth ?? 2) as 1 | 2 | 3 | 4,
            lineStyle: lineStyleOf(line.lineStyle),
            title: line.title,
            priceLineVisible: false,
            lastValueVisible: true,
          },
          pane,
        );
        s.setData(line.data.map((p) => ({ time: p.time, value: p.value })));
      }
      if (opts?.logScale) {
        const pane0 = usable.filter((l) => (l.pane ?? 0) === 0);
        const hasNonPositive = pane0.some((l) => l.data.some((p) => p.value <= 0));
        if (hasNonPositive) {
          debugLog('warn', 'chart', 'log scale skipped: non-positive pane-0 values');
        } else {
          c.priceScale('right').applyOptions({ mode: PriceScaleMode.Logarithmic });
        }
      }
      c.timeScale().fitContent();
    },

    fitContent() {
      chart?.timeScale().fitContent();
    },

    resize() {
      chart?.applyOptions({ autoSize: true });
    },
  };

  return api;
}
